import { debugLogger } from './DebugLogger';

export type InputCommand = 'primary' | 'cancel' | 'menu';
export type InputSource = 'pointer' | 'keyboard';

export interface GameInputEvent {
  command: InputCommand;
  source: InputSource;
  timestamp: number;
}

export interface InputDebugState {
  enabled: boolean;
  locked: boolean;
  lockDepth: number;
  lockReasons: Record<string, number>;
  lastCommand: InputCommand | null;
  lastSource: InputSource | null;
  emittedCount: number;
  ignoredDisabledCount: number;
  ignoredLockedCount: number;
  ignoredThrottledCount: number;
  lastIgnoredReason: 'disabled' | 'locked' | 'throttled' | null;
  lastIgnoredCommand: InputCommand | null;
  lastIgnoredSource: InputSource | null;
  listenerCount: number;
}

export type InputListener = (event: GameInputEvent) => void;

const DEFAULT_INPUT_INTERVAL_MS = 120;
const DEFAULT_LOCK_REASON = 'manual';
const POINTER_BUTTON_PRIMARY = 0;
const EDITABLE_SELECTOR =
  'input, textarea, select, [contenteditable]:not([contenteditable="false"])';

export class InputManager {
  private readonly listeners = new Set<InputListener>();
  private readonly minIntervalMs: number;
  private enabled = true;
  private readonly lockReasons = new Map<string, number>();
  private lastInputAt = 0;
  private lastCommand: InputCommand | null = null;
  private lastSource: InputSource | null = null;
  private emittedCount = 0;
  private ignoredDisabledCount = 0;
  private ignoredLockedCount = 0;
  private ignoredThrottledCount = 0;
  private lastIgnoredReason: InputDebugState['lastIgnoredReason'] = null;
  private lastIgnoredCommand: InputCommand | null = null;
  private lastIgnoredSource: InputSource | null = null;

  public constructor(
    private readonly target: HTMLElement,
    options: { minIntervalMs?: number } = {}
  ) {
    this.minIntervalMs = options.minIntervalMs ?? DEFAULT_INPUT_INTERVAL_MS;
    this.target.addEventListener('pointerup', this.handlePointerUp);
    window.addEventListener('keydown', this.handleKeyDown);
  }

  public subscribe(listener: InputListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  public setEnabled(enabled: boolean): void {
    if (this.enabled !== enabled) {
      debugLogger.log('input', 'setEnabled', { enabled });
    }
    this.enabled = enabled;
  }

  public lock(reason = DEFAULT_LOCK_REASON): void {
    const normalizedReason = normalizeLockReason(reason);
    const nextCount = (this.lockReasons.get(normalizedReason) ?? 0) + 1;
    this.lockReasons.set(normalizedReason, nextCount);
    debugLogger.log('input', 'lock', {
      reason: normalizedReason,
      count: nextCount,
      lockDepth: this.getLockDepth()
    });
  }

  public unlock(reason = DEFAULT_LOCK_REASON): void {
    const normalizedReason = normalizeLockReason(reason);
    const currentCount = this.lockReasons.get(normalizedReason) ?? 0;
    if (currentCount <= 1) {
      this.lockReasons.delete(normalizedReason);
      debugLogger.log('input', 'unlock', {
        reason: normalizedReason,
        count: 0,
        lockDepth: this.getLockDepth()
      });
      return;
    }

    const nextCount = currentCount - 1;
    this.lockReasons.set(normalizedReason, nextCount);
    debugLogger.log('input', 'unlock', {
      reason: normalizedReason,
      count: nextCount,
      lockDepth: this.getLockDepth()
    });
  }

  public getDebugState(): InputDebugState {
    const lockReasons = Object.fromEntries([...this.lockReasons.entries()].sort());
    return {
      enabled: this.enabled,
      locked: this.isLocked(),
      lockDepth: this.getLockDepth(),
      lockReasons,
      lastCommand: this.lastCommand,
      lastSource: this.lastSource,
      emittedCount: this.emittedCount,
      ignoredDisabledCount: this.ignoredDisabledCount,
      ignoredLockedCount: this.ignoredLockedCount,
      ignoredThrottledCount: this.ignoredThrottledCount,
      lastIgnoredReason: this.lastIgnoredReason,
      lastIgnoredCommand: this.lastIgnoredCommand,
      lastIgnoredSource: this.lastIgnoredSource,
      listenerCount: this.listeners.size
    };
  }

  private isLocked(): boolean {
    return this.lockReasons.size > 0;
  }

  private getLockDepth(): number {
    return [...this.lockReasons.values()].reduce((sum, count) => sum + count, 0);
  }

  public destroy(): void {
    this.listeners.clear();
    this.target.removeEventListener('pointerup', this.handlePointerUp);
    window.removeEventListener('keydown', this.handleKeyDown);
  }

  private readonly handlePointerUp = (event: PointerEvent): void => {
    if (event.button !== POINTER_BUTTON_PRIMARY) {
      return;
    }

    this.emit('primary', 'pointer', event.timeStamp);
  };

  private readonly handleKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat || event.isComposing || this.isEditableEventTarget(event.target)) {
      return;
    }

    const command = this.resolveKeyboardCommand(event);
    if (!command) {
      return;
    }

    event.preventDefault();
    this.emit(command, 'keyboard', event.timeStamp);
  };

  private resolveKeyboardCommand(event: KeyboardEvent): InputCommand | null {
    switch (event.key) {
      case ' ':
      case 'Enter':
        return 'primary';
      case 'Escape':
      case 'Backspace':
        return 'cancel';
      case 'm':
      case 'M':
        return 'menu';
      default:
        return null;
    }
  }

  private isEditableEventTarget(target: EventTarget | null): boolean {
    return target instanceof Element && target.closest(EDITABLE_SELECTOR) !== null;
  }

  private emit(command: InputCommand, source: InputSource, timestamp: number): void {
    if (!this.enabled) {
      this.recordIgnoredInput('disabled', command, source);
      debugLogger.log('input', 'ignored disabled input', { command, source });
      return;
    }

    if (this.isLocked()) {
      this.recordIgnoredInput('locked', command, source);
      debugLogger.log('input', 'ignored locked input', {
        command,
        source,
        lockReasons: Object.fromEntries([...this.lockReasons.entries()].sort())
      });
      return;
    }

    if (timestamp - this.lastInputAt < this.minIntervalMs) {
      this.recordIgnoredInput('throttled', command, source);
      debugLogger.log('input', 'ignored throttled input', {
        command,
        source,
        elapsedMs: Math.max(0, Math.round(timestamp - this.lastInputAt)),
        minIntervalMs: this.minIntervalMs
      });
      return;
    }

    this.lastInputAt = timestamp;
    this.lastCommand = command;
    this.lastSource = source;
    this.emittedCount += 1;
    const inputEvent: GameInputEvent = { command, source, timestamp };
    debugLogger.log('input', 'emit input', {
      command,
      source,
      listenerCount: this.listeners.size
    });

    for (const listener of this.listeners) {
      listener(inputEvent);
    }
  }

  private recordIgnoredInput(
    reason: NonNullable<InputDebugState['lastIgnoredReason']>,
    command: InputCommand,
    source: InputSource
  ): void {
    switch (reason) {
      case 'disabled':
        this.ignoredDisabledCount += 1;
        break;
      case 'locked':
        this.ignoredLockedCount += 1;
        break;
      case 'throttled':
        this.ignoredThrottledCount += 1;
        break;
    }

    this.lastIgnoredReason = reason;
    this.lastIgnoredCommand = command;
    this.lastIgnoredSource = source;
  }
}

function normalizeLockReason(reason: string): string {
  const normalized = reason.trim();
  return normalized.length > 0 ? normalized : DEFAULT_LOCK_REASON;
}
